"use client";

import {
  Button,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
} from "@heroui/react";
import { Share2 } from "lucide-react";
import {
  FaFacebook,
  FaTwitter,
  FaWhatsApp,
  LinkIcon,
} from "@/components/icons";

interface EventShareButtonProps {
  eventName: string;
  eventSlug: string;
  city: string;
  country: string;
  shortDescription?: string;
  formattedDate: string;
}

// LinkedIn icon component
const LinkedInIcon = ({
  className = "",
  size = 16,
}: {
  className?: string;
  size?: number;
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
  >
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
);

export default function EventShareButton({
  eventName,
  eventSlug,
  city,
  country,
  shortDescription,
  formattedDate,
}: EventShareButtonProps) {
  // Prepare sharing data
  const shareUrl = `https://technomusicworld.com/events/${eventSlug}`;
  const shareTitle = `${eventName} | ${city}, ${country}`;
  const shareDescription =
    shortDescription || `Join us for ${eventName} on ${formattedDate}`;

  // Social sharing URLs
  const twitterShareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareTitle)}`;
  const facebookShareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
  const linkedinShareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
  const whatsappShareUrl = `https://wa.me/?text=${encodeURIComponent(`${shareTitle} - ${shareUrl}`)}`;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      // You could add a toast notification here
    } catch (err) {
      console.error("Failed to copy link:", err);
    }
  };

  return (
    <Dropdown>
      <DropdownTrigger>
        <Button
          color="default"
          variant="flat"
          size="lg"
          className="w-full rounded-md"
          startContent={<Share2 className="w-4 h-4" />}
        >
          Share Event
        </Button>
      </DropdownTrigger>
      <DropdownMenu aria-label="Share event on social media">
        <DropdownItem
          key="twitter"
          startContent={<FaTwitter className="w-4 h-4" />}
          onPress={() => window.open(twitterShareUrl, "_blank")}
        >
          Share on Twitter
        </DropdownItem>
        <DropdownItem
          key="facebook"
          startContent={<FaFacebook className="w-4 h-4" />}
          onPress={() => window.open(facebookShareUrl, "_blank")}
        >
          Share on Facebook
        </DropdownItem>
        <DropdownItem
          key="linkedin"
          startContent={<LinkedInIcon className="w-4 h-4" size={16} />}
          onPress={() => window.open(linkedinShareUrl, "_blank")}
        >
          Share on LinkedIn
        </DropdownItem>
        <DropdownItem
          key="whatsapp"
          startContent={<FaWhatsApp className="w-4 h-4" />}
          onPress={() => window.open(whatsappShareUrl, "_blank")}
        >
          Share on WhatsApp
        </DropdownItem>
        <DropdownItem
          key="copyLink"
          startContent={<LinkIcon className="w-4 h-4" />}
          onPress={handleCopyLink}
        >
          Copy Link
        </DropdownItem>
      </DropdownMenu>
    </Dropdown>
  );
}
